<template>
  <EdroneModal
    :open="open && !loading"
    :close-on-esc-key-press="false"
    :close-on-outside-click="false"
    :show-close-icon="isDev"
    :width="600"
    :index="43000"
    unique-id="MainLayoutBlockImport--modal"
    @close="open = false"
  >
    <template #title>{{ trans._integrationV2.blockImport.title }}</template>
    <div class="tw:flex tw:flex-col tw:gap-6">
      <p class="tw:body-2 tw:text-default-700">
        {{ trans._integrationV2.blockImport.desc1 }}
        <br />
        {{ trans._integrationV2.blockImport.desc2 }}
      </p>
      <div>
        <div class="tw:flex tw:gap-6">
          <div class="tw:mb-4 tw:w-1/2">
            <p class="tw:body-1 tw:mb-1">{{ trans._integrationV2.blockImport.declared }}</p>
            <EdroneBadge
              :label="`${declaredBase} ${trans._integrationV2.blockImport.contacts}`"
              class="tw:cursor-pointer"
              @click="selected = declaredBase"
            />
          </div>
          <div class="tw:mb-4 tw:border-s tw:border-default-200 tw:ps-6 tw:w-1/2">
            <p class="tw:body-1 tw:mb-1">{{ trans._integrationV2.blockImport.current }}</p>
            <EdroneBadge :label="`${currentCustomersBase} ${trans._integrationV2.blockImport.contacts}`" />
          </div>
        </div>
        <p class="tw:body-1 tw:mb-1">{{ trans._integrationV2.blockImport.toImport }}</p>
        <div class="tw:flex tw:gap-4 tw:mb-4">
          <EdroneSliderInput
            unique-id="MainLayoutBlockImport--slider-input"
            :min="0"
            :max="totalContacts"
            :step="1"
            :value="selected"
            @update-value="selected = $event"
          />
          <EdroneNumberInput
            unique-id="MainLayoutBlockImport--number-input"
            :value="selected"
            :max="totalContacts"
            :min="0"
            @update-value="selected = $event"
          />
        </div>
        <div
          class="tw:flex tw:gap-9 tw:px-5 tw:border tw:border-default-200 tw:rounded-sm tw:p-5"
          :class="priceTiers?.length ? '' : 'tw:border-red-500'"
        >
          <div class="tw:flex-1">
            <p class="tw:body-2 tw:mb-1">{{ trans._integrationV2.blockImport.invoice.current }}</p>
            <p class="tw:heading-1 tw:flex tw:items-center tw:gap-1">
              {{ getPrice(declaredBase).toFixed(2) }}
              <span class="tw:heading-2">{{ currency }}</span>
            </p>
          </div>
          <div class="tw:text-2xl tw:self-center">
            <font-awesome-icon icon="fa-light fa-arrow-right" />
          </div>
          <div class="tw:flex-1">
            <p class="tw:body-2 tw:mb-1">{{ trans._integrationV2.blockImport.invoice.new }}</p>
            <p class="tw:heading-1 tw:mb-2 tw:flex tw:items-center tw:gap-1">
              {{ invoicePrice.toFixed(2) }}
              <span class="tw:heading-2">{{ currency }}</span>
            </p>
            <EdroneBadge :label="`+ ${(invoicePrice - getPrice(declaredBase)).toFixed(2)} ${currency}`" size="small" />
          </div>
        </div>
      </div>
      <p class="tw:body-2 tw:text-default-700">
        {{ trans._integrationV2.blockImport.info }}
      </p>
      <div class="tw:flex tw:gap-2">
        <EdroneButton
          class="flex-fill"
          type="solid"
          unique-id="MainLayoutBlockImport--import-button"
          @click="handleConfirm"
        >
          {{ trans._integrationV2.blockImport.confirm }}
        </EdroneButton>
      </div>
    </div>
  </EdroneModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, Ref } from "vue";
import EdroneModal from "edrone-components/EdroneModal.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneSliderInput from "edrone-components/EdroneSliderInput.vue";
import EdroneNumberInput from "edrone-components/EdroneNumberInput.vue";
import axios from "vue-app/utils/axios";
import {
  getIntegrationSynchronizationProgress,
  postIntegrationApprove,
} from "vue-app/api/integration/synchronization.ts";
import { DTOIntegrationSynchronizationProgressResponse, DTOTierPrice } from "vue-app/api/dto";

const isDev = window.EDRONE_CONTEXT.user.isDev;
const open = ref(false);
const loading = ref(true);
const currency = window.EDRONE_CONTEXT.user.currency;

const synchronizationProgress = ref<null | DTOIntegrationSynchronizationProgressResponse>(null);
const priceTiers: Ref<null | DTOTierPrice[]> = ref(null);
const declaredBase = ref(0);
const currentCustomersBase = ref(0);
const totalContacts = ref(0);

const selected = ref(0);

const invoicePrice = computed(() => {
  return getPrice(selected.value);
});

const handleConfirm = async () => {
  if (!synchronizationProgress.value?.integrationSynchronizationQueueId) {
    return;
  }
  open.value = false;
  try {
    await postIntegrationApprove(synchronizationProgress.value.integrationSynchronizationQueueId, selected.value);
    window.showToast("Contacts are being imported", "success");
  } catch (e) {
    console.error(e);
    window.showToast("An error occurred approving the import", "error");
  }
};

const getPrice = (value: number) => {
  return (
    priceTiers.value?.find((tier) => {
      return (
        (tier.fromUnits === null || tier.fromUnits <= value) && (tier.upToUnits === null || tier.upToUnits >= value)
      );
    })?.price || 0
  );
};

onMounted(async () => {
  synchronizationProgress.value = await getIntegrationSynchronizationProgress();
  const customers = synchronizationProgress.value?.types?.find((type) => type.name === "CUSTOMERS");
  if (
    !synchronizationProgress.value.approved &&
    synchronizationProgress.value.approvalRequiredReasons?.includes("DECLARED_BASE_EXCEEDED") &&
    customers
  ) {
    declaredBase.value = customers?.declaredBase || 0;
    currentCustomersBase.value = customers?.currentCustomersBase || 0;
    totalContacts.value = customers?.customersToImport || 0;
    selected.value = totalContacts.value;

    const { data } = await axios.get<DTOTierPrice[]>("/billing/price-tiers");
    priceTiers.value = data || [];

    loading.value = false;
    // window.EDRONE_CONTEXT.user.roles.ROLE_SUPER_AGENT / ROLE_DB ?
    open.value = true;
  }
});
</script>
